import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  ArrowRight,
  Calendar, 
  Phone,
  CheckCircle2,
  AlertCircle,
  Stethoscope,
  Pill
} from 'lucide-react';
import { treatmentAreas, getTreatmentBySlug } from '@/data/treatmentAreas';

const TreatmentDetailPage = () => {
  const { slug } = useParams();
  const treatment = getTreatmentBySlug(slug);

  if (!treatment) {
    return <Navigate to="/treatment-areas" replace />;
  }

  // Get adjacent treatments for navigation
  const currentIndex = treatmentAreas.findIndex(t => t.slug === slug);
  const prevTreatment = currentIndex > 0 ? treatmentAreas[currentIndex - 1] : null;
  const nextTreatment = currentIndex < treatmentAreas.length - 1 ? treatmentAreas[currentIndex + 1] : null;

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-muted py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-primary">Home</Link>
            <span className="text-muted-foreground">/</span>
            <Link to="/treatment-areas" className="text-muted-foreground hover:text-primary">Treatment Areas</Link>
            <span className="text-muted-foreground">/</span>
            <span className="text-foreground font-medium">{treatment.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-hero py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/treatment-areas" 
            className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Treatment Areas
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-6">
            {treatment.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl">
            {treatment.shortDescription}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground mb-4">Overview</h2>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {treatment.overview}
                </p>
              </div>

              {/* Symptoms */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-destructive" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-foreground">Symptoms</h2>
                  </div>
                  <ul className="space-y-3">
                    {treatment.symptoms.map((symptom, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Causes */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                      <Stethoscope className="w-6 h-6 text-warning" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-foreground">Causes</h2>
                  </div>
                  <ul className="space-y-3">
                    {treatment.causes.map((cause, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-warning mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{cause}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Treatment Options */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                      <Pill className="w-6 h-6 text-success" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-foreground">Treatment Options</h2>
                  </div>
                  <ul className="space-y-3">
                    {treatment.treatments.map((treatmentOption, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{treatmentOption}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* CTA Card */}
              <Card className="bg-primary text-primary-foreground border-0 shadow-xl sticky top-24">
                <CardContent className="p-8">
                  <h3 className="font-display text-xl font-bold mb-4">
                    Get Expert Treatment
                  </h3>
                  <p className="text-primary-foreground/90 mb-6">
                    Our fellowship-trained specialists are here to help. Schedule your consultation today.
                  </p>
                  <div className="space-y-3">
                    <Button variant="white" size="lg" className="w-full" asChild>
                      <a 
                        href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="w-5 h-5 mr-2" />
                        Schedule Now
                      </a>
                    </Button>
                    <Button variant="heroOutline" size="lg" className="w-full" asChild>
                      <a href="tel:321-500-4263">
                        <Phone className="w-5 h-5 mr-2" />
                        321.500.4263
                      </a>
                    </Button>
                  </div>
                  <p className="text-sm text-primary-foreground/70 mt-4 text-center">
                    Same-day appointments available
                  </p>
                </CardContent>
              </Card>

              {/* Other Conditions */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                    Other Treatment Areas
                  </h3>
                  <ul className="space-y-2">
                    {treatmentAreas
                      .filter(t => t.slug !== slug)
                      .slice(0, 6)
                      .map((t) => (
                        <li key={t.slug}>
                          <Link 
                            to={`/treatment-areas/${t.slug}`}
                            className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2"
                          >
                            <ArrowRight className="w-3 h-3" />
                            {t.title}
                          </Link>
                        </li>
                      ))}
                  </ul>
                  <Link 
                    to="/treatment-areas"
                    className="inline-flex items-center gap-2 text-primary font-medium text-sm mt-4 hover:gap-3 transition-all"
                  >
                    View All Treatment Areas
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-16 pt-8 border-t border-border">
            {prevTreatment ? (
              <Link 
                to={`/treatment-areas/${prevTreatment.slug}`}
                className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <div>
                  <p className="text-sm">Previous</p>
                  <p className="font-medium text-foreground">{prevTreatment.title}</p>
                </div>
              </Link>
            ) : <div />}
            {nextTreatment ? (
              <Link 
                to={`/treatment-areas/${nextTreatment.slug}`}
                className="flex items-center gap-3 text-right text-muted-foreground hover:text-primary transition-colors"
              >
                <div>
                  <p className="text-sm">Next</p>
                  <p className="font-medium text-foreground">{nextTreatment.title}</p>
                </div>
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TreatmentDetailPage;
